// CloudKeeper Org Services Portal — Amazon Inspector API client.
// Uses portalFetch from portalApi.js (JWT stored as portal_token).
// Mirrors macieApi.js: every call resolves to the {success,data} envelope.
//
// portalFetch gates on `response.ok`, so the 201 that POST /config, POST /refresh
// and POST /roles/status answer resolves exactly like a 200 — no call here needs a
// status special case. Any non-2xx instead REJECTS with an Error carrying the
// backend's `error` string, which is the only signal a caller gets: there is no
// status code on the thrown Error. Where a specific status is part of an
// endpoint's contract rather than a fault, it is called out on that function.
import { portalFetch } from './portalApi';

const BASE = '/api/v1/securityhub/inspector';

// --- config ---

// Resolves with `data: null` (HTTP 200) for a tenant that has never enabled
// Inspector. That is the setup-wizard signal, NOT an error — a rejection here
// means a real failure.
export const getInspectorConfig = () =>
  portalFetch(`${BASE}/config`);

// body: { homeRegion, linkingRegions, memberAccountIds, resourceTypes }
export const enableInspector = (body) =>
  portalFetch(`${BASE}/config`, {
    method: 'POST',
    body: JSON.stringify(body),
  });

export const disableInspector = () =>
  portalFetch(`${BASE}/config`, { method: 'DELETE' });

export const updateInspectorRegions = (linkingRegions) =>
  portalFetch(`${BASE}/config/regions`, {
    method: 'PUT',
    body: JSON.stringify({ linkingRegions }),
  });

// Inspector's analogue of Macie's discovery toggle: which resource kinds the
// scan covers. The backend validates each string against its own vocabulary and
// rejects the whole request on an unknown one, so this is not a free-form list.
export const updateInspectorResourceTypes = (resourceTypes) =>
  portalFetch(`${BASE}/config/resource-types`, {
    method: 'PUT',
    body: JSON.stringify({ resourceTypes }),
  });

// --- members ---

export const listInspectorMembers = () =>
  portalFetch(`${BASE}/members`);

// No memberEmails counterpart to addMacieMembers: the flat peer model enables
// Inspector in each account directly, so there is no invitation to address and no
// root-email resolution step before enrollment.
export const addInspectorMembers = (accountIds) =>
  portalFetch(`${BASE}/members`, {
    method: 'POST',
    body: JSON.stringify({ accountIds }),
  });

// Account only, unlike retryMacieMember's (account, region) pair: one retry
// re-drives every failed cell the account owns across all linking regions.
export const retryInspectorMember = (accountId) =>
  portalFetch(`${BASE}/members/retry`, {
    method: 'POST',
    body: JSON.stringify({ accountId }),
  });

export const removeInspectorMember = (accountId) =>
  portalFetch(`${BASE}/members/${encodeURIComponent(accountId)}`, {
    method: 'DELETE',
  });

// --- findings ---

export const listInspectorFindings = (params = {}) => {
  const qs = new URLSearchParams();
  // severity and status are the only multi-valued filters; the handler splits
  // them on "," and drops blanks. accountId is SINGLE-valued here (macieApi's
  // equivalent is the plural `account` list) — passing a comma-joined list would
  // be matched literally and return nothing.
  if (params.severity?.length) qs.set('severity', params.severity.join(','));
  if (params.status?.length) qs.set('status', params.status.join(','));
  if (params.accountId) qs.set('accountId', params.accountId);
  if (params.type) qs.set('type', params.type);
  if (params.search) qs.set('search', params.search);
  // Cursor drives keyset pagination; dropping it pins every page to the first.
  if (params.cursor) qs.set('cursor', params.cursor);
  if (params.limit) qs.set('limit', String(params.limit));
  const q = qs.toString();
  return portalFetch(`${BASE}/findings${q ? `?${q}` : ''}`);
};

// Accepts either the row's UUID `id` or its `findingArn` — the backend matches on
// both. Encoding is what makes the ARN form work at all: an unencoded
// "arn:aws:inspector2:...:finding/abc" carries slashes that would split the path
// and miss the single-segment "{id}" route, i.e. a 404 for every ARN lookup.
export const getInspectorFinding = (id) =>
  portalFetch(`${BASE}/findings/${encodeURIComponent(id)}`);

// --- refresh (the three-part polling contract) ---

// No "kind" discriminator in the body, unlike triggerMacieRefresh: Inspector
// caches only findings, so there is nothing to choose between.
export const triggerInspectorRefresh = () =>
  portalFetch(`${BASE}/refresh`, { method: 'POST' });

// Currently running refresh job for this tenant (data: null when none, including
// for an unconfigured tenant) — lets a dashboard re-attach its progress bar after
// navigation or reload. Takes no `kind` parameter, again unlike Macie's.
export const getInspectorActiveRefreshJob = () =>
  portalFetch(`${BASE}/refresh/active`);

// Rejects with "record not found" once a job ages out of the table; poll loops
// should stop on rejection rather than retrying forever.
export const getInspectorRefreshJob = (jobId) =>
  portalFetch(`${BASE}/refresh/${encodeURIComponent(jobId)}`);

// --- posture ---

// REJECTS (HTTP 404, "Amazon Inspector is not configured") when the tenant has
// not enabled Inspector, deliberately: an empty summary would render identically
// to "Inspector is on and everything is clean". This diverges from
// getMaciePostureSummary, which answers an unconfigured tenant with zeroes.
//
// The rejection is left intact rather than mapped to null here — callers that
// want the wizard should read the enabled/disabled state from getInspectorConfig,
// which is the endpoint that reports it without erroring.
export const getInspectorSummary = () =>
  portalFetch(`${BASE}/summary`);

// --- role permissions ("Fix roles", props for RolePermissionsPanel) ---
// Prism keeps a scoped inline policy on each enrolled account's switch role. The
// scan is async: POST starts (or re-joins) a job, GET polls it. Fix is
// fire-and-forget — re-run a scan to see the outcome.

export const startInspectorRolesScan = () =>
  portalFetch(`${BASE}/roles/status`, { method: 'POST' });

export const getInspectorRolesJob = (jobId) =>
  portalFetch(`${BASE}/roles/status/${encodeURIComponent(jobId)}`);

export const fixInspectorRoles = () =>
  portalFetch(`${BASE}/roles/fix`, { method: 'POST' });
